import type { ChatResponse, Product, RenderableResponse, StreamStage } from '@/types/chat';
import type { OverviewEchoContext } from '@/types/widget';
import { newClientMessageId } from '@/utils/uuid';
import { classify, getErrorCode } from './error-policy';

/**
 * Locally tracked message kinds. Welcome and echo are visual-only —
 * they never appear in the API request history sent to the backend.
 * Streaming is in-flight only and never persisted.
 */
export type ChatMessage =
  | { id: string; type: 'welcome'; createdAt: string }
  | { id: string; type: 'echo'; createdAt: string; context: OverviewEchoContext }
  | { id: string; type: 'user'; createdAt: string; text: string }
  | {
      id: string;
      type: 'assistant';
      createdAt: string;
      response: RenderableResponse;
      /** It arrived live, during this visit (not restored from history): typed out before the reader. */
      reveal?: boolean;
    }
  | {
      id: string;
      type: 'streaming';
      createdAt: string;
      /** What has come so far, in order: the text as it is written, the cards where they go. */
      blocks: StreamBlock[];
      /** The backend's current stage, from its status events, until the text starts. */
      stage?: StreamStage;
    }
  | {
      id: string;
      type: 'failed';
      createdAt: string;
      /** 失败的原文，重试时重发它。 */
      text: string;
      /** 后端错误码，用于查友好文案。网络故障时是 NETWORK_ERROR。 */
      code?: string;
      retriable: boolean;
    };

export function makeWelcomeMessage(): ChatMessage {
  return { id: newClientMessageId(), type: 'welcome', createdAt: new Date().toISOString() };
}

export function makeEchoMessage(context: OverviewEchoContext): ChatMessage {
  return {
    id: newClientMessageId(),
    type: 'echo',
    createdAt: new Date().toISOString(),
    context,
  };
}

export function makeUserMessage(text: string): ChatMessage {
  return {
    id: newClientMessageId(),
    type: 'user',
    createdAt: new Date().toISOString(),
    text,
  };
}

export function makeAssistantMessage(response: ChatResponse, reveal = false): ChatMessage {
  return {
    id: newClientMessageId(),
    type: 'assistant',
    createdAt: new Date().toISOString(),
    response,
    ...(reveal && { reveal }),
  };
}

export type StreamBlock =
  | { kind: 'text'; md: string }
  | { kind: 'cards'; skus: string[]; products: Product[] };

export function makeStreamingMessage(): ChatMessage {
  return {
    id: newClientMessageId(),
    type: 'streaming',
    createdAt: new Date().toISOString(),
    blocks: [],
  };
}

export function makeFailedMessage(text: string, error: unknown): ChatMessage {
  return {
    id: newClientMessageId(),
    type: 'failed',
    createdAt: new Date().toISOString(),
    text,
    ...(getErrorCode(error) ? { code: getErrorCode(error) } : {}),
    retriable: classify(error).retriable,
  };
}
